import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  FUTURES_ASSET_CLASS_OPTIONS,
  FUTURES_ORDER_STATE_OPTIONS,
  FUTURES_ORDER_STATE_LABELS,
  FUTURES_ORDER_TREND_OPTIONS,
  FUTURES_ORDER_TREND_LABELS,
  FUTURES_OVERRIDE_OPTIONS,
  MARKET_ASSET_CLASS_LABELS,
  MEMBER_ROLE_LABELS,
  MEMBER_ROLE_OPTIONS,
  buildPageParams,
  defaultOrderTimeRange,
  formatAmount,
  formatAreaCode,
  formatBettingRatio,
  formatOddsPercent,
  formatPhoneEmail,
  parseOverrideMethodLabel,
  validateOrderTimeRange,
} from './futuresOrderConstants';
import {
  formatSignedProfitLoss,
  orderTrendClassName,
} from '../../utils/orderDisplayFormat';

const DEFAULT_RANGE = defaultOrderTimeRange();

const EMPTY_FILTERS = {
  state: '',
  orderTrend: '',
  roleId: '',
  orderSerialId: '',
  roundCode: '',
  memberIdsCsv: '',
  topInviterId: '',
  inviterId: '',
  areaCode: '',
  phoneEmail: '',
  assetClass: '',
  overrideMethod: '',
  periodType: '',
  dailyOrderCount: '',
  minBettingRatio: '',
  maxBettingRatio: '',
  orderTimeFromInput: DEFAULT_RANGE.orderTimeFromInput,
  orderTimeToInput: DEFAULT_RANGE.orderTimeToInput,
  contractTimeFromInput: '',
  contractTimeToInput: '',
};

function OrderResultCell({ row }) {
  if (!row || row.state === 'BOOKED') {
    return '—';
  }
  if (row.state === 'CANCELLED') {
    return '已撤單';
  }
  const trendCode = row.settleTrend || row.orderTrend;
  const trend =
    FUTURES_ORDER_TREND_LABELS[row.settleTrend] ||
    FUTURES_ORDER_TREND_LABELS[row.orderTrend] ||
    row.settleTrend ||
    '—';
  return (
    <div className="order-result-cell">
      <div className={orderTrendClassName(trendCode)}>{trend}</div>
      <div>買入價格：{row.startPrice != null ? Number(row.startPrice).toFixed(6) : row.orderPrice != null ? Number(row.orderPrice).toFixed(6) : '—'}</div>
      <div>結算價格：{row.settlePrice != null ? Number(row.settlePrice).toFixed(6) : '—'}</div>
    </div>
  );
}

export default function FuturesOrdersPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.futuresOrdersPage(buildPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validateOrderTimeRange(
      filters.orderTimeFromInput,
      filters.orderTimeToInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    loadRecords(1, reset);
  }

  async function handleExport(exportCurrentPageOnly) {
    const rangeError = validateOrderTimeRange(
      filters.orderTimeFromInput,
      filters.orderTimeToInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setExporting(true);
    setError('');
    try {
      const params = {
        ...buildPageParams(page, filters),
        exportCurrentPageOnly,
        fileName: 'futures_order',
      };
      await adminApi.exportFuturesOrdersExcel(params);
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  async function handleConfirmCancel() {
    if (!cancelTarget?.id) {
      return;
    }
    setCancelling(true);
    setError('');
    try {
      await adminApi.cancelFuturesOrder({ id: cancelTarget.id });
      setCancelTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '撤單失敗');
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="page-card">
      <h2>交割訂單</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="orderState">訂單狀態</label>
          <select
            id="orderState"
            value={filters.state}
            onChange={(e) => updateFilter('state', e.target.value)}
          >
            {FUTURES_ORDER_STATE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="orderTrend">投注類型</label>
          <select
            id="orderTrend"
            value={filters.orderTrend}
            onChange={(e) => updateFilter('orderTrend', e.target.value)}
          >
            {FUTURES_ORDER_TREND_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="roleId">帳號類型</label>
          <select
            id="roleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {MEMBER_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="orderSerialId">訂單 ID</label>
          <input
            id="orderSerialId"
            value={filters.orderSerialId}
            onChange={(e) => updateFilter('orderSerialId', e.target.value)}
            placeholder="請輸入交割訂單 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="roundCode">週期 ID</label>
          <input
            id="roundCode"
            value={filters.roundCode}
            onChange={(e) => updateFilter('roundCode', e.target.value)}
            placeholder="請輸入週期 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="memberIdsCsv">用戶 ID</label>
          <input
            id="memberIdsCsv"
            value={filters.memberIdsCsv}
            onChange={(e) => updateFilter('memberIdsCsv', e.target.value)}
            placeholder="多個用戶 ID 以逗號分隔"
          />
        </div>

        <div className="form-field">
          <label htmlFor="topInviterId">頂級上級用戶 ID</label>
          <input
            id="topInviterId"
            value={filters.topInviterId}
            onChange={(e) => updateFilter('topInviterId', e.target.value)}
            placeholder="請輸入頂級上級用戶 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="inviterId">直屬上級用戶 ID</label>
          <input
            id="inviterId"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
            placeholder="請輸入直屬上級用戶 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="areaCode">國碼</label>
          <input
            id="areaCode"
            value={filters.areaCode}
            onChange={(e) => updateFilter('areaCode', e.target.value)}
            placeholder="請輸入國碼"
          />
        </div>

        <div className="form-field">
          <label htmlFor="phoneEmail">用戶郵箱/手機號碼</label>
          <input
            id="phoneEmail"
            value={filters.phoneEmail}
            onChange={(e) => updateFilter('phoneEmail', e.target.value)}
            placeholder="請輸入用戶郵箱/手機號碼"
          />
        </div>

        <div className="form-field">
          <label htmlFor="orderTimeFrom">委託時間起</label>
          <input
            id="orderTimeFrom"
            type="datetime-local"
            value={filters.orderTimeFromInput}
            onChange={(e) => updateFilter('orderTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="orderTimeTo">委託時間迄</label>
          <input
            id="orderTimeTo"
            type="datetime-local"
            value={filters.orderTimeToInput}
            onChange={(e) => updateFilter('orderTimeToInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="assetClass">商品類型</label>
          <select
            id="assetClass"
            value={filters.assetClass}
            onChange={(e) => updateFilter('assetClass', e.target.value)}
          >
            {FUTURES_ASSET_CLASS_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="overrideMethod">場控設置</label>
          <select
            id="overrideMethod"
            value={filters.overrideMethod}
            onChange={(e) => updateFilter('overrideMethod', e.target.value)}
          >
            {FUTURES_OVERRIDE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="dailyOrderCount">當日總投注次數</label>
          <input
            id="dailyOrderCount"
            value={filters.dailyOrderCount}
            onChange={(e) => updateFilter('dailyOrderCount', e.target.value)}
            placeholder="請輸入當日總投注次數"
          />
        </div>

        <div className="form-field">
          <label htmlFor="contractTimeFrom">合約時間起</label>
          <input
            id="contractTimeFrom"
            type="datetime-local"
            value={filters.contractTimeFromInput}
            onChange={(e) => updateFilter('contractTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="contractTimeTo">合約時間迄</label>
          <input
            id="contractTimeTo"
            type="datetime-local"
            value={filters.contractTimeToInput}
            onChange={(e) => updateFilter('contractTimeToInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label>投注比例 (%)</label>
          <div className="range-inputs">
            <input
              type="number"
              min="0"
              step="0.000001"
              value={filters.minBettingRatio}
              onChange={(e) => updateFilter('minBettingRatio', e.target.value)}
              placeholder="最小 %"
            />
            <span>—</span>
            <input
              type="number"
              min="0"
              step="0.000001"
              value={filters.maxBettingRatio}
              onChange={(e) => updateFilter('maxBettingRatio', e.target.value)}
              placeholder="最大 %"
            />
          </div>
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(true)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（當前頁面）'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(false)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（全部資料）'}
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table data-table--wide">
          <thead>
            <tr>
              <th>交割訂單 ID</th>
              <th>合約時間</th>
              <th>用戶 ID</th>
              <th>頂級用戶 ID</th>
              <th>直屬上級 ID</th>
              <th>帳號類型</th>
              <th>國碼</th>
              <th>用戶郵箱/手機號碼</th>
              <th>投注類型</th>
              <th>週期 ID</th>
              <th>產品名稱_週期</th>
              <th>商品類型</th>
              <th>訂單狀態</th>
              <th>場控設置</th>
              <th>結果</th>
              <th>當日總投注次數</th>
              <th>下單金額</th>
              <th>手續費</th>
              <th>盈虧金額</th>
              <th>投注比例</th>
              <th>交易前帳戶餘額</th>
              <th>交易後帳戶餘額</th>
              <th>盈利率</th>
              <th>違規利潤金額</th>
              <th>委託時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={26} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => {
                const profitLoss = formatSignedProfitLoss(row.profitLoss);
                const trendLabel =
                  FUTURES_ORDER_TREND_LABELS[row.orderTrend] || row.orderTrend || '—';

                return (
                <tr key={row.id}>
                  <td className="mono-cell">{row.orderSerialId || '—'}</td>
                  <td>{formatDateTime(row.roundEndTime)}</td>
                  <td>{row.memberId ?? '—'}</td>
                  <td>{row.topInviterId ?? '—'}</td>
                  <td>{row.inviterId ?? '—'}</td>
                  <td>{MEMBER_ROLE_LABELS[row.roleId] || row.roleId || '—'}</td>
                  <td>{formatAreaCode(row)}</td>
                  <td>{formatPhoneEmail(row)}</td>
                  <td>
                    <span className={orderTrendClassName(row.orderTrend)}>{trendLabel}</span>
                  </td>
                  <td className="mono-cell">{row.roundCode || '—'}</td>
                  <td>{row.productNamePeriod || '—'}</td>
                  <td>
                    {row.assetClass
                      ? MARKET_ASSET_CLASS_LABELS[row.assetClass] || row.assetClass
                      : '—'}
                  </td>
                  <td>
                    {FUTURES_ORDER_STATE_LABELS[row.state] || row.state || '—'}
                  </td>
                  <td>{parseOverrideMethodLabel(row.overrideMethod)}</td>
                  <td>
                    <OrderResultCell row={row} />
                  </td>
                  <td>{row.dailyOrderCount ?? '—'}</td>
                  <td>{formatAmount(row.orderAmount)}</td>
                  <td>{formatAmount(row.fee)}</td>
                  <td className={profitLoss.className}>{profitLoss.text}</td>
                  <td>{formatBettingRatio(row.bettingRatio)}</td>
                  <td>{formatAmount(row.beforeBalance)}</td>
                  <td>{formatAmount(row.balance)}</td>
                  <td>{formatOddsPercent(row.orderOdds)}</td>
                  <td>—</td>
                  <td>{formatDateTime(row.orderTime)}</td>
                  <td className="action-cell">
                    {row.state === 'BOOKED' ? (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setCancelTarget(row)}
                      >
                        撤單
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        title="確認撤單"
        detail={cancelTarget ? `訂單 ${cancelTarget.orderSerialId || cancelTarget.id}` : ''}
        message="確定要撤銷此尚未結算的交割訂單？款項將退回會員錢包。"
        confirmLabel="確認撤單"
        confirming={cancelling}
        onConfirm={handleConfirmCancel}
        onClose={() => !cancelling && setCancelTarget(null)}
      />
    </div>
  );
}
